import IconComponent from "@mdi/react";
import {
  Fragment,
  PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import { useQuery } from "react-query";
import { useDispatch } from "react-redux";

import { mdiChevronLeft, mdiChevronRight, mdiClose } from "@mdi/js";
import type mapboxgl from "mapbox-gl";
// eslint-disable-next-line no-restricted-imports -- the same intentional dual-write useMapClickSelection makes, for cross-tab redux-state-sync; see EVA-6684
import { setSelectedWells as setSelectedWellsRedux } from "store/features/map/mapSlice";
import { useMapStore } from "stores/map";
import { selectActivePanel, usePanelStore } from "stores/panelStore";
import {
  WellBounds,
  WellCardPreview,
  previewUwi,
  selectWellCardPanelMaximizedOnScreen,
  selectWellCardPanelOpen,
  selectWellCardPreview,
  useWellCardPanelStore
} from "stores/wellCardPanel";
import styled from "styled-components";

import { useUserEvents } from "hooks";

import { WellDetailCardData, getWellDetailCard } from "api/data";

import { Uwi } from "models";
import { EntityKind } from "models/entityKind";

import { Tooltip } from "components/base";
import ContextMenu from "components/charts/toggles/ContextMenu";
import { useMapContext } from "components/map/hooks/useMapContext";

import { HEADER_FIELDS, WELL_CARD_VIEW_SOURCE, reportedName } from "./constants";
import { MISSING_VALUE } from "./styles";
import { Span, wellCardSpan } from "./utils/wellCardSpan";

/** `@mdi/react` draws `1.5 x size` rem, and this app's root is 62.5%. */
const CLOSE_ICON_SIZE = 0.93; // 14px, per the design
const CHEVRON_ICON_SIZE = 1; // 15px glyph in a 20px hit area
const CARD_MIN_WIDTH_PX = 256;
const CARD_MAX_WIDTH_PX = 360;
/** Until the card is measured: its floor width and a typical height. */
const CARD_ESTIMATE: Size = { width: CARD_MIN_WIDTH_PX, height: 200 };
/** antd's offset plus the hidden arrow's padding, on every side of the anchor. */
const ANCHOR_GAP_PX = 20;
/** Keeps the card off the well's outline, which is drawn wider than its line. */
const WELL_PAD_PX = 6;
/** Just under antd's modals and drawers (1000), so their mask covers it. */
const PREVIEW_Z_INDEX = 999;

type Placement = "top" | "bottom" | "right" | "left";
const PLACEMENTS: readonly Placement[] = ["right", "left", "top", "bottom"];
type Size = { width: number; height: number };
type Box = { left: number; top: number; right: number; bottom: number };

/**
 * The five rows, in the design's order. The well's name is the sixth field,
 * promoted to the header row.
 */
const ROWS: readonly { label: string; property?: string }[] = [
  { label: "API#/UWI" },
  { label: "Operator", property: "Header.Operator_Short" },
  { label: "Zone", property: "Geology.Zone" },
  { label: "Vintage (Year-Month)", property: "Dates.Vintage_YearMonth" },
  { label: "Status", property: "Header.Status" }
];

/**
 * The tile carries only the UWI, so the popup needs its own small fetch. Its
 * field array is deliberately its own — the Well Ticket's Header request is
 * built from the org's whole Header category and can never share this key.
 */
function useWellPreviewData(uwi: Maybe<string>) {
  return useQuery(
    ["wellCardPanelPreview", uwi],
    async () => {
      const response = await getWellDetailCard({
        uwid: uwi,
        fields: [...HEADER_FIELDS],
        entityKind: EntityKind.Well
      });
      return response.data as WellDetailCardData;
    },
    {
      enabled: !!uwi,
      retry: false,
      // The key carries the uwi, so without this every arrow press empties the
      // card and refills it. The rows dim instead while the next well lands.
      keepPreviousData: true
    }
  );
}

/**
 * The map's click-to-preview card (design 5b, "Compact card"): a 218px light
 * card placed clear of the clicked well, with the well's name in its header, five
 * label/value rows, and a teal View Well Card button. A click that lands on
 * several wells adds a pager to the footer.
 *
 * Two clicks to open the full card: the map click publishes this popup, and
 * only the button retargets the panel.
 */
export function WellCardPreviewPopup(): JSX.Element {
  const preview = useWellCardPanelStore(selectWellCardPreview);
  const setPreview = useWellCardPanelStore((s) => s.setPreview);
  const setPreviewIndex = useWellCardPanelStore((s) => s.setPreviewIndex);
  const setPreviewOffScreen = useWellCardPanelStore((s) => s.setPreviewOffScreen);
  const setTargetUwi = useWellCardPanelStore((s) => s.setTargetUwi);
  const setOpen = useWellCardPanelStore((s) => s.setOpen);
  const cardOpen = useWellCardPanelStore(selectWellCardPanelOpen);
  const cardMaximized = useWellCardPanelStore(selectWellCardPanelMaximizedOnScreen);
  const { mapbox } = useMapContext();
  const dispatch = useDispatch();
  const { sendWellCardViewedEvent } = useUserEvents();

  const shownUwi = previewUwi(preview);
  const { data, isLoading, isPreviousData } = useWellPreviewData(shownUwi);

  // Kept with the preview's opening point, which paging carries over, so a new
  // preview starts from its own point.
  const [followed, setFollowed] =
    useState<
      Maybe<{ anchor: WellCardPreview["point"]; x: number; y: number; inView: boolean }>
    >(null);
  const openedAt = preview?.point;
  const lngLat = preview?.lngLat;
  useEffect(() => {
    if (!mapbox || !openedAt || !lngLat) return;
    const follow = () => {
      const { x, y } = mapbox.project(lngLat);
      const { clientWidth, clientHeight } = mapbox.getContainer();
      setFollowed({
        anchor: openedAt,
        x,
        y,
        inView: x >= 0 && y >= 0 && x <= clientWidth && y <= clientHeight
      });
    };
    mapbox.on("move", follow);
    return () => {
      mapbox.off("move", follow);
    };
  }, [mapbox, openedAt, lngLat]);
  const following = followed?.anchor === openedAt ? followed : null;
  // Hidden, not closed: it comes back when the well is panned back into view.
  const isOffScreen = !!following && !following.inView;
  useEffect(() => {
    setPreviewOffScreen(isOffScreen);
  }, [isOffScreen, setPreviewOffScreen]);

  /*
   * Escape closes the popup, and the same press clears the map's selection and
   * highlight (useMapClickSelection). Only the section line's binding stands
   * down while the popup is up. It closes off screen too, or the hidden preview
   * would keep its well highlighted.
   *
   * A plain bubble-phase listener that consumes nothing: swallowing the event
   * here would take Escape away from every other listener in the application,
   * antd's modals and dropdowns included.
   */
  const open = !!preview;
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setPreview(null);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, setPreview]);

  // The settings panels cover the whole app.
  const settingsPanelOpen = usePanelStore(selectActivePanel) !== null;
  useEffect(() => {
    if (settingsPanelOpen) setPreview(null);
  }, [settingsPanelOpen, setPreview]);

  // A fullscreen widget draws an opaque sheet over a map that stays mounted at full
  // size, so only the `inert` it puts on the grid says the map is covered.
  const [mapCovered, setMapCovered] = useState(false);
  useEffect(() => {
    if (!open || !mapbox) return;
    const check = () => setMapCovered(!!mapbox.getContainer().closest("[inert]"));
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.body, {
      subtree: true,
      attributes: true,
      attributeFilter: ["inert"]
    });
    return () => observer.disconnect();
  }, [open, mapbox]);

  // Its X, Escape, a click on another well or a settings panel closes it.
  const cardRef = useRef<HTMLDivElement>(null);

  // Offset sizes, not the bounding box: antd's zoom-in scales that while it opens.
  const [cardSize, setCardSize] = useState(CARD_ESTIMATE);
  useLayoutEffect(() => {
    const width = cardRef.current?.offsetWidth;
    const height = cardRef.current?.offsetHeight;
    if (!width || !height) return;
    if (width === cardSize.width && height === cardSize.height) return;
    setCardSize({ width, height });
  });

  // The well being shown, not the well that was clicked: after paging those
  // are different, and the card has to open on the one being read.
  const openCard = useCallback(() => {
    if (!shownUwi) return;
    // Identifiers and the pager only, all of it already in the store. The
    // values on the card stay off the event: they are the customer's data, and
    // anything the analytics needs can be read from the uwi.
    sendWellCardViewedEvent({
      uwi: shownUwi,
      entityKind: EntityKind.Well,
      source: WELL_CARD_VIEW_SOURCE.previewPopup,
      pagerPosition: (preview?.activeIndex ?? 0) + 1,
      pagerCount: preview?.candidates.length ?? 1
    });
    setTargetUwi(shownUwi);
    setOpen(true);
  }, [shownUwi, preview, sendWellCardViewedEvent, setTargetUwi, setOpen]);

  // Paging moves the map's selection with the popup, so the outline is always
  // on the well being described. This is the write the click itself makes.
  const showCandidate = useCallback(
    (index: number) => {
      const candidate = preview?.candidates[index];
      if (!candidate) return;
      setPreviewIndex(index);
      const selection = { [candidate.Uwi]: candidate };
      useMapStore.getState().setSelectedWells(selection);
      dispatch(setSelectedWellsRedux(selection));
    },
    [preview, setPreviewIndex, dispatch]
  );

  // Kept with the preview's opening point, so it rides along as the well is
  // re-projected and a new preview opens on its own well.
  const rootRef = useRef<HTMLDivElement>(null);
  const [dragged, setDragged] =
    useState<Maybe<{ anchor: WellCardPreview["point"]; dx: number; dy: number }>>(null);
  const offset =
    dragged && dragged.anchor === preview?.point ? dragged : { dx: 0, dy: 0 };

  // Map points are in the canvas's pixels, and the canvas sits inside this
  // root past the map's toolbars, so the anchor is moved by that gap.
  const [canvasGap, setCanvasGap] = useState({ x: 0, y: 0 });
  useLayoutEffect(() => {
    const root = rootRef.current?.getBoundingClientRect();
    const canvas = mapbox?.getContainer?.().getBoundingClientRect();
    if (!root || !canvas) return;
    const x = canvas.left - root.left;
    const y = canvas.top - root.top;
    if (x === canvasGap.x && y === canvasGap.y) return;
    setCanvasGap({ x, y });
  });

  const onHeaderPointerDown = useCallback(
    (event: ReactPointerEvent) => {
      if (!preview || event.button !== 0) return;
      if ((event.target as HTMLElement).closest("button")) return;
      // Read once, at the press: the map is not resized mid-drag.
      const bounds = rootRef.current?.getBoundingClientRect();
      const box = cardRef.current?.getBoundingClientRect();
      if (!bounds || !box) return;
      const anchor = preview.point;
      const startX = event.clientX - offset.dx;
      const startY = event.clientY - offset.dy;
      // The card hangs off its anchor, so it is the card that must stay on the map.
      const minDx = offset.dx + bounds.left - box.left;
      const maxDx = offset.dx + bounds.right - box.right;
      const minDy = offset.dy + bounds.top - box.top;
      const maxDy = offset.dy + bounds.bottom - box.bottom;
      // Captured so a fast drag that outruns the header still moves the card,
      // and the map under the pointer never sees the gesture.
      const handle = event.currentTarget as HTMLElement;
      handle.setPointerCapture(event.pointerId);
      const move = (moveEvent: PointerEvent) =>
        setDragged({
          anchor,
          dx: clamp(moveEvent.clientX - startX, minDx, maxDx),
          dy: clamp(moveEvent.clientY - startY, minDy, maxDy)
        });
      const end = () => {
        handle.removeEventListener("pointermove", move);
        handle.removeEventListener("pointerup", end);
        handle.removeEventListener("pointercancel", end);
      };
      handle.addEventListener("pointermove", move);
      handle.addEventListener("pointerup", end);
      handle.addEventListener("pointercancel", end);
    },
    [preview, offset.dx, offset.dy]
  );

  // A maximized card or a fullscreen widget covers the map, and the popup would sit on top.
  if (!preview || !shownUwi || isOffScreen || cardMaximized || mapCovered) return <></>;
  const point = following ?? preview.point;

  const count = preview.candidates.length;
  const index = preview.activeIndex;
  const formattedUwi = new Uwi().toFormatted(shownUwi);

  const valueFor = (property?: string): string => {
    if (!property) return formattedUwi ?? shownUwi;
    if (isLoading) return "…";
    const raw = data?.[property];
    return typeof raw === "string" && raw ? raw : MISSING_VALUE;
  };

  // The name is the header, so with none reported the header falls back to the
  // uwi rather than being left blank.
  const rawName = reportedName(data?.["Header.WellName"], data?.["Header.EntityName"]);
  const wellName = rawName ?? formattedUwi ?? shownUwi;

  const bounds = preview.wellBounds?.[shownUwi];
  const container = mapbox?.getContainer?.();
  const map = container && {
    width: container.clientWidth,
    height: container.clientHeight
  };
  // Projected on every render, which the camera follow above triggers.
  const well = mapbox && map && bounds ? wellRect(mapbox, bounds, map) : undefined;
  const target = well ?? { left: point.x, top: point.y, right: point.x, bottom: point.y };
  const shiftX = offset.dx + canvasGap.x;
  const shiftY = offset.dy + canvasGap.y;
  const position = well
    ? {
        x: well.left + shiftX,
        y: well.top + shiftY,
        width: well.right - well.left,
        height: well.bottom - well.top
      }
    : { x: point.x + shiftX, y: point.y + shiftY };
  const card = cardOpen ? wellCardSpan() : undefined;
  // In window pixels: the popup may hang past a narrow map over the widgets
  // beside it, as long as it stays on screen and off the well card. Chosen for
  // the well rather than the dragged card, so a drag never flips it.
  const canvas = container?.getBoundingClientRect();
  const placement = canvas
    ? choosePlacement(
        {
          left: target.left + canvas.left,
          top: target.top + canvas.top,
          right: target.right + canvas.left,
          bottom: target.bottom + canvas.top
        },
        cardSize,
        { width: window.innerWidth, height: window.innerHeight },
        card
      )
    : "right";

  return (
    <Root ref={rootRef} data-testid="well-card-preview-root">
      <ContextMenu
        overlayClassName="well-card-preview"
        zIndex={PREVIEW_Z_INDEX}
        visible
        allow
        placement={placement}
        position={position}
        content={
          <Card ref={cardRef} data-testid="well-card-preview">
            <Header
              onPointerDown={onHeaderPointerDown}
              data-testid="well-card-preview-header">
              <WellName title={wellName} data-testid="well-card-preview-name">
                {wellName}
              </WellName>
              <Tooltip title="Close">
                <CloseButton
                  type="button"
                  aria-label="Close"
                  onClick={() => setPreview(null)}
                  data-testid="well-card-preview-close">
                  <IconComponent path={mdiClose} size={CLOSE_ICON_SIZE} />
                </CloseButton>
              </Tooltip>
            </Header>
            <Body $stale={isPreviousData}>
              <Rows>
                {ROWS.map((row) => {
                  const value = valueFor(row.property);
                  return (
                    <Fragment key={row.label}>
                      <RowLabel>{row.label}:</RowLabel>
                      <RowValue title={value}>{value}</RowValue>
                    </Fragment>
                  );
                })}
              </Rows>
              <Footer>
                {/* Only when the click landed on more than one well. With a
                    single hit there is nothing to page through, and an inert
                    "1 of 1" would only crowd the button. */}
                {count > 1 && (
                  <Pager data-testid="well-card-preview-pager">
                    {/* The pager keeps a plain title: both buttons are
                        disabled at their end of the list, where a hover
                        tooltip has nothing to say and would not fire. */}
                    <PagerButton
                      type="button"
                      title="Previous well"
                      aria-label="Previous well"
                      disabled={index === 0}
                      onClick={() => showCandidate(index - 1)}
                      data-testid="well-card-preview-prev">
                      <IconComponent path={mdiChevronLeft} size={CHEVRON_ICON_SIZE} />
                    </PagerButton>
                    <PagerCount data-testid="well-card-preview-count">
                      {index + 1} of {count}
                    </PagerCount>
                    <PagerButton
                      type="button"
                      title="Next well"
                      aria-label="Next well"
                      disabled={index === count - 1}
                      onClick={() => showCandidate(index + 1)}
                      data-testid="well-card-preview-next">
                      <IconComponent path={mdiChevronRight} size={CHEVRON_ICON_SIZE} />
                    </PagerButton>
                  </Pager>
                )}
                <OpenButton
                  type="button"
                  onClick={openCard}
                  data-testid="well-card-preview-open">
                  View Well Card
                </OpenButton>
              </Footer>
            </Body>
          </Card>
        }
      />
    </Root>
  );
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

/**
 * The well's box on the map, padded off its outline. All four corners, since a
 * rotated map turns the box. Cut to the map, or a lateral running off it would
 * centre the card off it too.
 */
function wellRect(
  mapbox: mapboxgl.Map,
  [[west, south], [east, north]]: WellBounds,
  map: Size
): Box {
  const corners = [
    mapbox.project([west, south]),
    mapbox.project([west, north]),
    mapbox.project([east, south]),
    mapbox.project([east, north])
  ];
  const xs = corners.map(({ x }) => x);
  const ys = corners.map(({ y }) => y);
  return {
    left: Math.max(Math.min(...xs) - WELL_PAD_PX, 0),
    top: Math.max(Math.min(...ys) - WELL_PAD_PX, 0),
    right: Math.min(Math.max(...xs) + WELL_PAD_PX, map.width),
    bottom: Math.min(Math.max(...ys) + WELL_PAD_PX, map.height)
  };
}

/**
 * The first side, in order, where the card stays on screen and off the open
 * well card. Failing all four, the side it sticks out of least.
 */
function choosePlacement(target: Box, card: Size, screen: Size, cover: Maybe<Span>) {
  const midX = (target.left + target.right) / 2;
  const midY = (target.top + target.bottom) / 2;
  const across = { left: midX - card.width / 2, right: midX + card.width / 2 };
  const beside = { top: midY - card.height / 2, bottom: midY + card.height / 2 };
  const above = target.top - ANCHOR_GAP_PX;
  const below = target.bottom + ANCHOR_GAP_PX;
  const after = target.right + ANCHOR_GAP_PX;
  const before = target.left - ANCHOR_GAP_PX;
  const boxes: Record<Placement, Box> = {
    top: { ...across, top: above - card.height, bottom: above },
    bottom: { ...across, top: below, bottom: below + card.height },
    right: { ...beside, left: after, right: after + card.width },
    left: { ...beside, left: before - card.width, right: before }
  };
  // Only the well card's span across is known, so it counts as a full column.
  const overflow = ({ left, top, right, bottom }: Box) =>
    Math.max(0, -left) +
    Math.max(0, -top) +
    Math.max(0, right - screen.width) +
    Math.max(0, bottom - screen.height) +
    (cover ? Math.max(0, Math.min(right, cover.right) - Math.max(left, cover.left)) : 0);
  return PLACEMENTS.reduce((best, placement) =>
    overflow(boxes[placement]) < overflow(boxes[best]) ? placement : best
  );
}

const Root = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 1;

  > * {
    pointer-events: auto;
  }
`;

/*
 * 256px: design 5b draws this card at 218, which reads too tight against the
 * map once it is in place. Its proportions and type rules are still 5b's,
 * every step scaled together — see the sizes on the rows and the button.
 *
 * That 256 is the FLOOR rather than the width. The card holds a well's name,
 * its operator and its zone, and none of those have a length the design can
 * decide: cut to a fixed width they came out as ellipses, which is the one
 * thing a card meant to identify a well must not do. So it fits its longest
 * line and stops at a ceiling, because a single pathological operator name
 * should not drag a 256px card across the map. At the ceiling the old
 * behaviour resumes - the value ellipsizes and keeps its `title`.
 *
 * The card carries its own surface: the border,
 * the radius and the floating shadow that make this popup the deliberate
 * exception to the no-shadows rule.
 *
 * NOT put on antd's popover inner, which would be the obvious place: that box
 * is sized by antd's positioning, and a surface drawn on it stretches with it.
 * The inner is made transparent for this popup instead, under
 * `.well-card-preview` in antd.css.
 */
const Card = styled.div`
  width: max-content;
  min-width: ${CARD_MIN_WIDTH_PX}px;
  max-width: ${CARD_MAX_WIDTH_PX}px;
  /* The hover tooltip's surface (WellDetail), so the two read as one family:
     a translucent wash over the map rather than an opaque card. Everything
     else about the popup - layout, order, type - is its own. */
  background: radial-gradient(
    rgba(0, 0, 0, 0.2) 0%,
    rgba(0, 0, 0, 0.15) 80%,
    rgba(0, 0, 0, 0.1) 100%
  );
  -webkit-backdrop-filter: blur(4px);
  backdrop-filter: blur(4px);
  border-radius: var(--border-radius);
  box-shadow: 0 8px 24px rgba(9, 30, 66, 0.2), 0 2px 6px rgba(9, 30, 66, 0.1);
  overflow: hidden;
  font-family: "Open Sans", sans-serif;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 8px 9px 7px 12px;
  border-bottom: 1px solid var(--fog);
  cursor: grab;
  /* A drag would otherwise select the name as it goes. */
  user-select: none;
  touch-action: none;
`;

const WellName = styled.span`
  flex: 1;
  min-width: 0;
  /* Pinned to the app font, like the button: only the rows take Open Sans. */
  font-family: var(--fontFamily);
  /* A step above the rows: the name is the card's heading. */
  font-size: 14px;
  font-weight: 700;
  color: var(--white);
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const CloseButton = styled.button`
  border: 0;
  padding: 0;
  background: transparent;
  color: var(--white);
  opacity: 0.75;
  cursor: pointer;
  display: inline-flex;
  flex: none;
  transition: opacity 150ms var(--ease);

  &:hover {
    opacity: 1;
  }
`;

// Dimmed, not blanked, while the next well's fields land: keepPreviousData is
// showing the previous well's values under the new well's name.
const Body = styled.div<{ $stale: boolean }>`
  padding: 8px 12px 11px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  opacity: ${(props) => (props.$stale ? 0.45 : 1)};
  transition: opacity 120ms linear;
`;

// The hover tooltip's grid (WellDetail), so the labels read down a right edge.
const Rows = styled.div`
  display: grid;
  grid-template-columns: max-content 1fr;
  align-items: flex-end;
  gap: 2px 8px;
  font-size: 12px;
  line-height: 1.4;
`;

const RowLabel = styled.span`
  text-align: right;
  /* The tooltip's label is an h5, which antd's reset puts at 500. */
  font-weight: 500;
  color: var(--white);
`;

const RowValue = styled.span`
  min-width: 0;
  margin-left: 5px;
  /* The hover tooltip's value colour, and nothing smaller than 12px anywhere
     on this card - the wash behind it costs some contrast. */
  color: #cdfffa;
  font-weight: bold;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const Footer = styled.div`
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Pager = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex: none;
`;

const PagerButton = styled.button`
  border: 0;
  padding: 0;
  background: transparent;
  color: var(--white);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: var(--border-radius);
  transition: background 150ms var(--ease);

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.2);
  }

  &:disabled {
    cursor: default;
    color: rgba(255, 255, 255, 0.35);
  }
`;

const PagerCount = styled.span`
  font-family: var(--fontMono);
  font-size: 12px;
  color: var(--white);
  white-space: nowrap;
`;

const OpenButton = styled.button`
  flex: 1;
  height: 30px;
  border: none;
  border-radius: var(--border-radius);
  background: var(--color-primary);
  color: var(--white);
  /* Pinned to what it inherited before the card took Open Sans. */
  font-family: var(--fontFamily);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 150ms var(--ease);

  &:hover {
    background: var(--color-primary-hover);
  }
`;
